import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TIPOS = [
  { code: "33", label: "Factura Electrónica (33)" },
  { code: "34", label: "Factura Exenta (34)" },
  { code: "39", label: "Boleta (39)" },
  { code: "52", label: "Guía de Despacho (52)" },
  { code: "56", label: "Nota Débito (56)" },
  { code: "61", label: "Nota Crédito (61)" },
];

export default function AdminDocumentsPage() {
  const [docs, setDocs] = useState([]);
  const [comuna, setComuna] = useState("ALL");
  const [tipo, setTipo] = useState("ALL");
  const [comunas, setComunas] = useState([]);

  const load = async () => {
    try {
      const params = {};
      if (comuna !== "ALL") params.comuna = comuna;
      if (tipo !== "ALL") params.tipo_dte = tipo;
      const { data } = await api.get("/admin/documents", { params });
      setDocs(data);
    } catch {
      toast.error("Error al cargar documentos");
    }
  };

  useEffect(() => {
    api.get("/comunas").then(({ data }) => setComunas(data));
  }, []);

  useEffect(() => {
    load();
  }, [comuna, tipo]);

  const totals = useMemo(() => {
    const t = docs.reduce((s, d) => s + (d.mnt_total || 0), 0);
    return { count: docs.length, total: t };
  }, [docs]);

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto p-6 md:p-10">
        <PageHeader
          eyebrow="Administración · Vista global"
          title="Todos los documentos"
          description="Facturas, guías de despacho y notas de crédito procesadas por todos los clientes."
        />

        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white border border-slate-200 p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">
              Documentos
            </div>
            <div className="font-display font-black text-3xl text-[#0B1B3D] tabular-nums">
              {totals.count}
            </div>
          </div>
          <div className="bg-white border border-slate-200 p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">
              Monto total
            </div>
            <div className="font-display font-black text-3xl text-[#0B1B3D] tabular-nums">
              ${totals.total.toLocaleString("es-CL")}
            </div>
          </div>
          <div className="bg-white border border-slate-200 p-4 flex flex-col justify-center">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">
              Filtros activos
            </div>
            <div className="text-sm text-slate-700">
              {comuna !== "ALL" ? `Comuna: ${comuna}` : "Todas las comunas"}
              {" · "}
              {tipo !== "ALL" ? `Tipo: ${tipo}` : "Todos los DTE"}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3 mb-4">
          <Select value={comuna} onValueChange={setComuna}>
            <SelectTrigger data-testid="admin-docs-comuna-filter">
              <SelectValue placeholder="Filtrar por comuna" />
            </SelectTrigger>
            <SelectContent className="bg-white max-h-72">
              <SelectItem value="ALL">Todas las comunas</SelectItem>
              {comunas.map((c) => (
                <SelectItem key={c.id} value={c.nombre}>
                  {c.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={tipo} onValueChange={setTipo}>
            <SelectTrigger data-testid="admin-docs-tipo-filter">
              <SelectValue placeholder="Filtrar por tipo DTE" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="ALL">Todos los tipos</SelectItem>
              {TIPOS.map((t) => (
                <SelectItem key={t.code} value={t.code}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0B1B3D] text-white">
              <tr>
                <Th>Fecha</Th>
                <Th>Tipo</Th>
                <Th>Folio</Th>
                <Th>Cliente</Th>
                <Th>Receptor</Th>
                <Th>Dirección</Th>
                <Th>Comuna</Th>
                <Th className="text-right">Región</Th>
                <Th className="text-right">Total</Th>
                <Th>Lote</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {docs.length === 0 && (
                <tr>
                  <td colSpan={10} className="p-8 text-center text-slate-500">
                    Sin documentos que mostrar
                  </td>
                </tr>
              )}
              {docs.map((d, i) => (
                <tr
                  key={`${d.batch_id}-${d.folio}-${i}`}
                  className="hover:bg-slate-50"
                  data-testid={`admin-doc-row-${i}`}
                >
                  <td className="px-3 py-2 text-xs tabular-nums whitespace-nowrap">
                    {d.created_at
                      ? new Date(d.created_at).toLocaleDateString("es-CL")
                      : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase bg-[#0B1B3D] text-white">
                      DTE {d.tipo_dte}
                    </span>
                  </td>
                  <td className="px-3 py-2 tabular-nums font-semibold">
                    {d.folio}
                  </td>
                  <td className="px-3 py-2 text-slate-600 text-xs">
                    {d.user_email}
                  </td>
                  <td className="px-3 py-2 text-slate-800">
                    {d.rzn_soc_receptor}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">
                    {d.delivery_direccion}
                  </td>
                  <td className="px-3 py-2 font-semibold text-[#D95A40]">
                    {d.delivery_comuna}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {d.region ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">
                    ${(d.mnt_total || 0).toLocaleString("es-CL")}
                  </td>
                  <td className="px-3 py-2">
                    <Link
                      to={`/batches/${d.batch_id}`}
                      className="text-[#D95A40] hover:underline text-xs font-semibold"
                    >
                      Ver lote →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`px-3 py-2.5 text-left text-[11px] font-bold uppercase tracking-[0.15em] ${className}`}
    >
      {children}
    </th>
  );
}
